/**
 * MasterCounterpartyPicker — P3-10.2.
 *
 * Universal suggest-as-you-type combobox backed by /api/counterparties.
 * Returns the FULL master counterparty object on selection. Ideal as a
 * drop-in replacement for the legacy 5-source dropdowns in wizards.
 *
 *   <MasterCounterpartyPicker
 *      value={form.counterparty_id}
 *      onSelect={(cp) => setForm({...form, counterparty_id: cp.id, ...})}
 *      placeholder="Search companies..."
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Users, ChevronDown, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_PILL = {
  supplier: 'bg-blue-100 text-blue-700',
  client: 'bg-emerald-100 text-emerald-700',
  partner: 'bg-amber-100 text-amber-700',
  customer: 'bg-violet-100 text-violet-700',
  contact: 'bg-slate-100 text-slate-700',
  lead: 'bg-red-100 text-red-700',
  jv_partner: 'bg-purple-100 text-purple-700',
};

const MasterCounterpartyPicker = ({
  value,
  onSelect,
  placeholder = 'Search master counterparty…',
  disabled = false,
  typeFilter = null, // optional: restrict to one type
  testId = 'master-cp-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Hydrate label when value is provided but selected not yet loaded
  useEffect(() => {
    if (!value || (selected && selected.id === value)) return;
    (async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const r = await axios.get(
          `${API_URL}/api/counterparties/${value}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSelected(r.data);
      } catch {
        /* ignore */
      }
    })();
  }, [value, selected]);

  const auth = useMemo(() => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (query) params.append('q', query);
        if (typeFilter) params.append('type', typeFilter);
        params.append('limit', '20');
        const r = await axios.get(`${API_URL}/api/counterparties?${params.toString()}`, auth);
        setRows(r.data?.rows || []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open, typeFilter, auth]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (cp) => {
    setSelected(cp);
    setOpen(false);
    setQuery('');
    if (onSelect) onSelect(cp);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    setQuery('');
    if (onSelect) onSelect(null);
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-400 disabled:opacity-50"
        data-testid={`${testId}-trigger`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="h-4 w-4 text-slate-400 shrink-0" />
          {selected ? (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-slate-800 truncate">{selected.name}</span>
              <div className="flex gap-1 shrink-0">
                {(selected.types || []).slice(0, 3).map((t) => (
                  <span key={t} className={`text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded ${TYPE_PILL[t] || TYPE_PILL.contact}`}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {selected && (
            <span onClick={handleClear} className="text-slate-400 hover:text-slate-600" data-testid={`${testId}-clear`}>
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </div>
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
          data-testid={`${testId}-panel`}
        >
          <div className="sticky top-0 border-b border-slate-100 bg-white p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, TRN, email…"
                className="w-full rounded border border-slate-200 bg-slate-50 px-7 py-1.5 text-sm focus:border-slate-400 focus:bg-white focus:outline-none"
                data-testid={`${testId}-input`}
              />
            </div>
          </div>
          {loading && <div className="p-3 text-xs text-slate-500">Searching…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-3 text-xs text-slate-400">
              {query ? 'No match' : 'Type to search or browse all counterparties'}
            </div>
          )}
          <ul>
            {rows.map((cp) => (
              <li key={cp.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(cp)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  data-testid={`${testId}-option-${cp.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800 truncate">{cp.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {cp.legal_name || '—'}{cp.country ? ` · ${cp.country}` : ''}{cp.tax_number ? ` · ${cp.tax_number}` : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {(cp.types || []).slice(0, 3).map((t) => (
                      <span key={t} className={`text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded ${TYPE_PILL[t] || TYPE_PILL.contact}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MasterCounterpartyPicker;
